import React from 'react';
import { BadgeCellValue } from '@components/data-table';
import { formatLocaleDateAtTime } from '@helpers/dateHelpers';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getManageCompetitionSessionStatusBadgeColor,
  getManageCompetitionSessionStatusName,
} from '@pages/Management/Competitions/Competitions/helpers';
import { PortalSessionNotes } from '../PortalSessionNotes';
import { IPortalSessionListItem } from './interfaces';

export const PortalSessionListItem: React.FC<IPortalSessionListItem> = ({
  session,
  onCompletedSessionClick,
  showParticipantCount = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const isClickable =
    session.status === 'completed' && onCompletedSessionClick !== undefined;

  const handleClick = (): void => {
    if (isClickable && onCompletedSessionClick) {
      onCompletedSessionClick(session);
    }
  };

  const showSeriesAndParticipantsLine =
    showParticipantCount &&
    session.status === 'completed' &&
    session.participantCount !== undefined;

  const isInactiveSession =
    session.status === 'cancelled' || session.status === 'not_held';
  const sessionNotes = isInactiveSession ? '' : (session.notes?.trim() ?? '');
  const cancellationReason = session.cancellationReason?.trim() ?? '';
  const showStatusBadge =
    session.status === 'in_progress' ||
    session.status === 'cancelled' ||
    session.status === 'not_held';

  const metaLine = showSeriesAndParticipantsLine
    ? [
        t('competitions:portal.details.sessionsPanel.seriesCount', {
          count: session.seriesCount,
        }),
        t('competitions:portal.details.sessionsTab.participantCount', {
          count: session.participantCount,
        }),
      ].join(' · ')
    : null;

  const content = (
    <Stack sx={{ gap: 0.75 }}>
      <Stack sx={{ gap: 0.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '.85rem',
              ...(isInactiveSession && { textDecoration: 'line-through' }),
            }}
          >
            {formatLocaleDateAtTime(session.heldOn)}
          </Typography>
          {showStatusBadge && (
            <BadgeCellValue
              label={getManageCompetitionSessionStatusName(
                session.status
              ).toLowerCase()}
              color={getManageCompetitionSessionStatusBadgeColor(
                session.status
              )}
            />
          )}
        </Box>
        {metaLine !== null ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '.75rem' }}
          >
            {metaLine}
          </Typography>
        ) : (
          !isInactiveSession &&
          session.seriesCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '.75rem' }}
            >
              {t('competitions:portal.details.sessionsPanel.seriesCount', {
                count: session.seriesCount,
              })}
            </Typography>
          )
        )}
      </Stack>
      {cancellationReason !== '' && (
        <PortalSessionNotes
          notes={cancellationReason}
          labelKey="competitions:portal.details.sessionsTab.cancellationReason.label"
        />
      )}
      {sessionNotes !== '' && <PortalSessionNotes notes={sessionNotes} />}
    </Stack>
  );

  if (isClickable) {
    return (
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        sx={{
          display: 'block',
          width: '100%',
          margin: 0,
          padding: 1,
          textAlign: 'left',
          cursor: 'pointer',
          border: 'none',
          borderRadius: 1,
          background: 'transparent',
          font: 'inherit',
          color: 'inherit',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {content}
      </Box>
    );
  }

  if (
    session.status === 'planned' ||
    session.status === 'in_progress' ||
    session.status === 'cancelled' ||
    session.status === 'not_held'
  ) {
    return (
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          cursor: 'default',
          ...(isInactiveSession && {
            opacity: 0.85,
          }),
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }}
      >
        {content}
      </Box>
    );
  }

  return content;
};
